/* Výpověď pojistné smlouvy — vzor podle ZEMĚ TENANTA.
 *
 *   CZ → § 2807 NOZ (zákon č. 89/2012 Sb.), česky — beze změny.
 *   SK → § 800 ods. 1 zákona č. 40/1964 Zb. Občiansky zákonník, slovensky
 *        (od 4. 10. 2026, majitel: „ano“ na slovenské znění výpovědi).
 *
 * Zavolá backend (/api/contracts/{id}/cancellation-letter) se zemí tenanta,
 * backend vyplní vzor (pojišťovna, klient, číslo smlouvy, datum) a tady se
 * otevře tiskové okno → poradce vytiskne / uloží PDF / zkopíruje text.
 * Vše escapované (XSS-safe).
 *
 * TŘI JAZYKY V JEDNOM OKNĚ — schválně:
 *   • dopis (text + nadpis + <html lang>) = jazyk POJIŠŤOVNY podle země
 *     (vietnamský poradce v Česku píše české pojišťovně česky),
 *   • tlačítka, titulek okna a poznámka „šablona“ = jazyk APPKY (t()),
 *   • poznámka se NETISKNE (@media print) — na papír jde jen dopis.
 *
 * STARÝ BACKEND (před redeployem na Emergentu) zemi nezná: tělo požadavku
 * ignoruje a vrátí český vzor bez pole `zeme`. Okno pak slovenskému poradci
 * řekne nahlas, že dostal český vzor — nic se netváří jako slovenské znění.
 * Zdroj a datum ověření slovenského práva: backend/routes/contract_lifecycle.py
 * (CANCEL_TEMPLATES_SK).
 */
import api from "@/lib/api";
import { normZeme, zemeTenanta } from "@/lib/tenantMena";
import { prekladacSmluv, textData } from "@/lib/smlouvy/textySmluv";
import { naDatum } from "@/lib/i18n/format";

const esc = (x) => String(x ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

/* Jazyk a nadpis DOPISU podle země — dopis jde do pojišťovny v její zemi,
 * proto to není text appky (t()), ale součást vzoru. Zrcadlí JAZYK_VZORU
 * a NADPIS_VZORU v backendu; platí, jen když backend pole nepošle (starý). */
export const JAZYK_DOPISU = Object.freeze({ CZ: "cs", SK: "sk" });
export const NADPIS_DOPISU = Object.freeze({ CZ: "Výpověď pojistné smlouvy", SK: "Výpoveď poistnej zmluvy" });
/* Starý backend vrací jen český vzor — právní základ podle jeho textu. */
const ZAKLAD_STAREHO_VZORU = Object.freeze({
  zivot: "§ 2805 a § 2806 zákona č. 89/2012 Sb., občanský zákoník",
  nezivot: "§ 2807 zákona č. 89/2012 Sb., občanský zákoník",
});

/**
 * Co backend doopravdy vrátil (čistá funkce).
 * @param {object} data       odpověď /cancellation-letter
 * @param {string} pozadovana země, o kterou jsme žádali (tenant)
 * @returns {{ zeme, jazyk, nadpis, zaklad, staryServer, nesoulad }}
 *   staryServer = backend nezná země (pole chybí) → vzor je vždy český,
 *   nesoulad    = dostali jsme jinou zemi, než jsme chtěli → upozornit.
 */
export function vysledekVypovedi(data, pozadovana) {
  const d = data && typeof data === "object" ? data : {};
  const chtena = normZeme(pozadovana);
  const staryServer = !d.zeme;
  const zeme = staryServer ? "CZ" : normZeme(d.zeme);
  const zaklad = d.pravni_zaklad
    || (staryServer ? ZAKLAD_STAREHO_VZORU[d.type === "pojisteni_zivot" ? "zivot" : "nezivot"] : "");
  return {
    zeme,
    jazyk: d.jazyk || JAZYK_DOPISU[zeme] || "cs",
    nadpis: d.nadpis || NADPIS_DOPISU[zeme] || NADPIS_DOPISU.CZ,
    zaklad,
    staryServer,
    nesoulad: zeme !== chtena,
  };
}

/**
 * Leží datum ukončení v minulosti (před dneškem)? Čistá funkce.
 *
 * Backend do dopisu dosadí výročí smlouvy TAK, JAK JE ULOŽENÉ — u starší
 * smlouvy s výročím 15. 1. 2026 by dopis vypovídal „ke dni 15.01.2026“,
 * tedy k datu, které už bylo (nález ověření 4. 10. 2026). Příští konec
 * pojistného období se tu schválně NEdopočítává — délku období zná jen
 * smlouva; okno to poradci jen řekne nahlas. Neznámé datum → false.
 */
export function datumVMinulosti(vstup, dnes = new Date()) {
  const d = naDatum(vstup);
  const t = naDatum(dnes);
  if (!d || !t) return false;
  const den = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return den(d) < den(t);
}

/**
 * HTML tiskového okna (čistá funkce — testovatelná bez window.open).
 * @param {object} data      odpověď backendu (letter, provider, client_name, …)
 * @param {object} vysledek  vysledekVypovedi(…)
 * @param {string} lang      jazyk appky (tlačítka, poznámka)
 * @param {{ datumUkonceni?: string, dnes?: Date }} [volby]  datum, které backend
 *        dosadil do dopisu (výročí / konec smlouvy); `dnes` jen pro testy
 */
export function htmlVypovedi(data, vysledek, lang = "cs", { datumUkonceni = null, dnes } = {}) {
  const t = prekladacSmluv(lang);
  const d = data && typeof data === "object" ? data : {};
  const v = vysledek || vysledekVypovedi(d, "CZ");
  const minulo = datumVMinulosti(datumUkonceni, dnes);
  const poznamky = [
    `<strong>${esc(t("smlouvaKarta.okno.sablona"))}</strong>`,
    v.zaklad ? `<div>${esc(t("smlouvaKarta.okno.zaklad", { zaklad: v.zaklad }))}</div>` : "",
    v.jazyk !== lang
      ? `<div>${esc(t("smlouvaKarta.okno.jazykDopisu", { jazyk: t(`smlouvaKarta.okno.jazyk.${v.jazyk}`, v.jazyk) }))}</div>`
      : "",
    v.zeme === "SK" ? `<div>${esc(t("smlouvaKarta.okno.lhutySK"))}</div>` : "",
    v.nesoulad ? `<div class="varovani" data-testid="vypoved-stary-server">${esc(t("smlouvaKarta.okno.staryServer"))}</div>` : "",
    minulo ? `<div class="varovani" data-testid="vypoved-datum-minulo">${esc(t("smlouvaKarta.okno.datumMinulo", { datum: textData(datumUkonceni, lang) }))}</div>` : "",
  ].filter(Boolean).join("\n        ");
  /* Text do alert() jde přes JSON.stringify (JS řetězec) a pak esc() — HTML
   * parser atributu &quot; vrátí na uvozovky dřív, než se spustí JS. */
  const zkopirovano = esc(JSON.stringify(t("smlouvaKarta.okno.zkopirovano")));
  return `
    <!DOCTYPE html>
    <html lang="${esc(v.jazyk)}"><head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <title>${esc(t("smlouvaKarta.okno.titulek", { pojistovna: d.provider || "", klient: d.client_name || "" }))}</title>
      <style>
        body { font-family: 'Times New Roman', serif; max-width: 680px; margin: 40px auto; padding: 30px; line-height: 1.6; color: #2A1A0E; }
        pre { white-space: pre-wrap; font-family: inherit; font-size: 13px; }
        h1 { font-size: 20px; border-bottom: 2px solid #B85C1F; padding-bottom: 8px; }
        .actions { margin-bottom: 16px; }
        .actions button { min-height: 44px; padding: 8px 16px; margin: 0 8px 8px 0; background: #B85C1F; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; }
        .actions button.secondary { background: #5e3a1c; }
        .pozn { font-family: system-ui, sans-serif; font-size: 13px; line-height: 1.5; background: #FFF6E5; border: 1px solid #E8C48A; border-radius: 8px; padding: 10px 14px; margin-bottom: 24px; color: #2A1A0E; }
        .pozn div { margin-top: 4px; }
        .pozn .varovani { color: #8A1C0F; font-weight: 600; }
        @media (max-width: 480px) { body { margin: 0 auto; padding: 16px; } }
        @media print { .actions, .pozn { display: none; } }
      </style>
    </head><body>
      <div class="actions">
        <button onclick="window.print()">${esc(t("smlouvaKarta.okno.tisk"))}</button>
        <button class="secondary" onclick="navigator.clipboard.writeText(document.getElementById('letter').innerText); alert(${zkopirovano});">${esc(t("smlouvaKarta.okno.kopirovat"))}</button>
        <button class="secondary" onclick="window.close()">${esc(t("smlouvaKarta.okno.zavrit"))}</button>
      </div>
      <div class="pozn" role="note" data-testid="vypoved-poznamka">
        ${poznamky}
      </div>
      <h1>${esc(v.nadpis)}</h1>
      <pre id="letter">${esc(d.letter)}</pre>
    </body></html>
  `;
}

/**
 * Vygeneruje výpověď a otevře tiskové okno.
 * @param {string} contractId
 * @param {{ lang?: string, zeme?: string, datumUkonceni?: string }} [volby] jazyk
 *   appky, země tenanta (bez ní se vezme z úložiště tenanta — lib/tenantMena.js)
 *   a datum ukončení ze smlouvy (okno upozorní, když už minulo)
 * @returns {Promise<ReturnType<typeof vysledekVypovedi>>}
 */
export async function openCancellationLetter(contractId, { lang = "cs", zeme, datumUkonceni = null } = {}) {
  const pozadovana = normZeme(zeme || zemeTenanta());
  const r = await api.post(`/api/contracts/${contractId}/cancellation-letter`, { zeme: pozadovana });
  const data = (r && r.data) || {};
  const vysledek = vysledekVypovedi(data, pozadovana);
  const w = window.open("", "_blank", "width=720,height=900,scrollbars=yes");
  if (!w) throw new Error("popup-blocked");
  w.document.write(htmlVypovedi(data, vysledek, lang, { datumUkonceni }));
  w.document.close();
  return vysledek;
}

/** Pojistné typy smluv — jen u nich dává výpověď do pojišťovny smysl.
 *
 * Seznam = typy, pro které backend MÁ vzor výpovědi
 * (backend/routes/contract_lifecycle.py → CANCEL_TEMPLATES / VYPOVEDITELNE_TYPY).
 * Od 4. 10. 2026 i pojištění odpovědnosti a ostatní neživotní (právní ochrana,
 * cestovní, podnikatelské). Shodu hlídá __tests__/druhySmluvSlozka.test.js —
 * tlačítko u typu bez vzoru by skončilo chybou 400. */
export const VYPOVEDITELNE_TYPY = Object.freeze([
  "pojisteni_auta", "pojisteni_majetek", "pojisteni_zivot", "pojisteni_odpovednost", "pojisteni_ostatni",
]);
export const isInsuranceType = (type) => VYPOVEDITELNE_TYPY.includes(type);
